import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Card, CardContent, Typography, Chip, Grid, Paper, Button, Alert, Checkbox, FormControlLabel, TextField, LinearProgress } from "@mui/material";
import AdminLayout from "../AdminLayout";
import ev from "../../services/eventV2Service";
import { useAuth } from "../../hooks/useAuth";

export default function EventTimelinePage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [selectedStep, setSelectedStep] = useState("registration");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [expenseRows, setExpenseRows] = useState([{ title: "", amount: "", vendor: "", file: null }]);

  useEffect(() => {
    const unsub = ev.listenEvent(eventId, (e) => setEvent(e));
    return () => unsub && unsub();
  }, [eventId]);

  useEffect(() => {
    async function load() {
      if (!user?.uid) return;
      const reg = await ev.getUserRegistration(eventId, user.uid);
      setRegistration(reg);
      const ex = await ev.getUserExpenses(eventId, user.uid);
      setExpenses(ex);
    }
    load();
  }, [eventId, user]);

  const workflow = registration?.workflow || {};
  const cutoff = Number(event?.cutoff || 0);

  const steps = [
    { key: "registration", name: "External Registration", status: workflow.registeredExternally ? "completed" : "pending" },
    { key: "team", name: "Team Submission", status: (registration?.team || []).length > 0 ? "completed" : "pending" },
    { key: "confirmation", name: "Organizer Confirmation", status: workflow.organizerConfirmed ? "completed" : "pending" },
    { key: "fund", name: "Fund Approval Request", status: workflow.fundApproved ? "approved" : (workflow.fundRequested ? "active" : "pending") },
    { key: "travel", name: "Travel Request", status: workflow.travelArranged ? "approved" : (workflow.travelRequested ? "active" : "pending") },
    { key: "accommodation", name: "Accommodation Request", status: workflow.accommodationArranged ? "approved" : (workflow.accommodationRequested ? "active" : "pending") },
    { key: "expenses", name: "Expense Submission", status: expenses.length > 0 ? "completed" : "pending" },
    { key: "reimbursement", name: "Reimbursement", status: workflow.reimbursementCompleted ? "completed" : "pending" }
  ];

  const progress = Math.round((steps.filter((s) => s.status === "completed" || s.status === "approved").length / steps.length) * 100);

  const refresh = async () => {
    if (!user?.uid) return;
    const reg = await ev.getUserRegistration(eventId, user.uid);
    setRegistration(reg);
    const ex = await ev.getUserExpenses(eventId, user.uid);
    setExpenses(ex);
  };

  const saveWorkflow = async (patch) => {
    try {
      setError("");
      setSuccess("");
      await ev.updateRegistrationWorkflowByUser(eventId, user.uid, patch);
      setSuccess("Updated");
      await refresh();
    } catch (e) {
      setError(e.message || "Failed to update");
    }
  };

  const addExpenseRow = () => {
    setExpenseRows((prev) => [...prev, { title: "", amount: "", vendor: "", file: null }]);
  };

  const removeExpenseRow = (idx) => {
    setExpenseRows((prev) => prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx));
  };

  const updateExpenseRow = (idx, patch) => {
    setExpenseRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const submitExpense = async () => {
    try {
      setError("");
      setSuccess("");
      const rows = expenseRows.filter((r) => r.title || r.amount || r.vendor || r.file);
      if (rows.length === 0) {
        setError("Add at least one expense");
        return;
      }
      for (const r of rows) {
        if (!r.title || !r.amount) {
          setError("Each expense must include title and amount");
          return;
        }
      }
      for (const r of rows) {
        await ev.addExpense(user.uid, eventId, { title: r.title, amount: Number(r.amount), vendor: r.vendor }, r.file || null);
      }
      setExpenseRows([{ title: "", amount: "", vendor: "", file: null }]);
      setSuccess("Expenses submitted");
      await refresh();
    } catch (e) {
      setError(e.message || "Failed to submit expense");
    }
  };

  const selected = steps.find((s) => s.key === selectedStep) || steps[0];

  return (
    <AdminLayout title="Event Timeline">
      <Box sx={{ p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <Card sx={{ borderTop: "6px solid #003366" }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#003366", mb: 2 }}>
              {event?.title || "Event"}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              Cutoff: {cutoff}% • Progress: {progress}%
            </Typography>
            <LinearProgress variant="determinate" value={progress} sx={{ mb: 2 }} />
            {!event || !user?.uid ? (
              <Typography color="text.secondary">Loading...</Typography>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {steps.map((st) => (
                      <Paper
                        key={st.key}
                        onClick={() => setSelectedStep(st.key)}
                        sx={{
                          p: 1.5,
                          cursor: "pointer",
                          border: selectedStep === st.key ? "2px solid #003366" : "1px solid #e5e7eb",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                      >
                        <Typography sx={{ fontWeight: 600 }}>{st.name}</Typography>
                        <Chip
                          label={st.status === "approved" ? "Approved" : st.status === "completed" ? "Completed" : st.status === "active" ? "Pending Approval" : "Pending"}
                          color={st.status === "approved" ? "info" : st.status === "completed" ? "success" : st.status === "active" ? "warning" : "default"}
                          size="small"
                        />
                      </Paper>
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12} md={7}>
                  <Paper sx={{ p: 2, border: "1px solid #e5e7eb", minHeight: 380 }}>
                    <Typography sx={{ fontWeight: 800, color: "#003366", mb: 2 }}>{selected.name}</Typography>

                    {selectedStep === "registration" && (
                      <Box>
                        <Button
                          variant="contained"
                          disabled={!event?.externalRegistrationLink}
                          onClick={() => window.open(event.externalRegistrationLink, "_blank", "noopener,noreferrer")}
                          sx={{ mr: 1 }}
                        >
                          Register Now
                        </Button>
                        <FormControlLabel
                          control={<Checkbox checked={!!workflow.registeredExternally} onChange={(e) => saveWorkflow({ registeredExternally: e.target.checked })} />}
                          label="I have registered on external portal"
                        />
                      </Box>
                    )}

                    {selectedStep === "team" && (
                      <Box>
                        <Typography color="text.secondary" sx={{ mb: 1 }}>
                          Added members: {(registration?.team || []).length}
                        </Typography>
                        <Button variant="contained" onClick={() => navigate(`/student/events-v2/${eventId}/register`)}>
                          Manage Team
                        </Button>
                      </Box>
                    )}

                    {selectedStep === "confirmation" && (
                      <FormControlLabel
                        control={<Checkbox checked={!!workflow.organizerConfirmed} onChange={(e) => saveWorkflow({ organizerConfirmed: e.target.checked })} />}
                        label="I received confirmation from external organizer"
                      />
                    )}

                    {selectedStep === "fund" && (
                      <Box>
                        <FormControlLabel
                          control={<Checkbox checked={!!workflow.fundRequested} onChange={(e) => saveWorkflow({ fundRequested: e.target.checked })} />}
                          label="Raise fund approval request"
                        />
                        <Typography color="text.secondary">Approved Amount: {workflow.fundAmount || 0}</Typography>
                        <Typography color="text.secondary">Remarks: {workflow.fundRemarks || "-"}</Typography>
                      </Box>
                    )}

                    {selectedStep === "travel" && (
                      <Box>
                        <FormControlLabel
                          control={<Checkbox checked={!!workflow.travelRequested} onChange={(e) => saveWorkflow({ travelRequested: e.target.checked })} />}
                          label="Request travel arrangement"
                        />
                        <Typography color="text.secondary">Travel arranged: {workflow.travelArranged ? "Yes" : "No"}</Typography>
                      </Box>
                    )}

                    {selectedStep === "accommodation" && (
                      <Box>
                        <FormControlLabel
                          control={<Checkbox checked={!!workflow.accommodationRequested} onChange={(e) => saveWorkflow({ accommodationRequested: e.target.checked })} />}
                          label="Request accommodation arrangement"
                        />
                        <Typography color="text.secondary">Accommodation arranged: {workflow.accommodationArranged ? "Yes" : "No"}</Typography>
                      </Box>
                    )}

                    {selectedStep === "expenses" && (
                      <Box>
                        <Typography color="text.secondary" sx={{ mb: 1 }}>
                          Add multiple expenses with title, amount, vendor, and bill.
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          {expenseRows.map((row, idx) => (
                            <Paper key={idx} sx={{ p: 1, border: "1px solid #e5e7eb" }}>
                              <Grid container spacing={1}>
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Expense Title"
                                    value={row.title}
                                    onChange={(e) => updateExpenseRow(idx, { title: e.target.value })}
                                  />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Amount"
                                    type="number"
                                    value={row.amount}
                                    onChange={(e) => updateExpenseRow(idx, { amount: e.target.value })}
                                  />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Vendor"
                                    value={row.vendor}
                                    onChange={(e) => updateExpenseRow(idx, { vendor: e.target.value })}
                                  />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <Button variant="outlined" component="label" fullWidth>
                                    Bill
                                    <input hidden type="file" onChange={(e) => updateExpenseRow(idx, { file: e.target.files?.[0] || null })} />
                                  </Button>
                                </Grid>
                                <Grid item xs={12}>
                                  <Button size="small" color="error" onClick={() => removeExpenseRow(idx)}>Remove</Button>
                                </Grid>
                              </Grid>
                            </Paper>
                          ))}
                        </Box>
                        <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                          <Button variant="outlined" onClick={addExpenseRow}>Add Expense Row</Button>
                          <Button variant="contained" onClick={submitExpense}>Submit All Expenses</Button>
                        </Box>
                        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                          {expenses.map((ex) => (
                            <Paper key={ex.id} sx={{ p: 1, border: "1px solid #e5e7eb" }}>
                              <Typography>{ex.title} • ₹{ex.amount} • {ex.vendor || "-"}</Typography>
                            </Paper>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {selectedStep === "reimbursement" && (
                      <Box>
                        <Typography color="text.secondary">Status: {workflow.reimbursementCompleted ? "Completed" : "Pending"}</Typography>
                        <Typography color="text.secondary">Student Name: {workflow.reimbursementStudentName || "-"}</Typography>
                        <Typography color="text.secondary">Bank Account: {workflow.reimbursementBankAccount || "-"}</Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
}
